import React, { useCallback, useMemo, useState } from 'react';
import * as api from '../api';
import Register from '../../hr/components/Register';
import { can, usePyMeta } from '../py';

// Stephanet's Grade Policy Setup group (Job Families, Positions, Position Grade Eligibility, Benefit Eligibility Rules): its register
// views over job_families, job_positions, grade_eligibility and benefit_rules. They drive the grade picker and the benefit table on the
// employee's Financial Details (HR Add Employee / Employee 360). Kept from this app (the user's decisions): a grade outside the allowed
// range needs a grade exception reason, and a rule points at a Benefits Setup benefit or a Payment Structure type instead of a fixed
// list of names; Not Eligible blocks that allowance / benefit in Compensation.

function usePolicy(pick) {
  const [setup, setSetup] = useState(null);
  const load = useCallback(() => api.gradeSetup().then(r => { setSetup(r); return pick(r); }), []); // eslint-disable-line react-hooks/exhaustive-deps
  return [setup, load];
}
const yes = { active: true };
const opt = rows => (rows || []).map(r => ({ value: String(r.id), label: r.name || r.code }));

export function JobFamilies() {
  const [meta] = usePyMeta();
  const [, load] = usePolicy(r => r.families);
  const res = useMemo(() => ({
    key: 'job_families', label: 'Job Family', plural: 'Job Families', title: r => r.name, load, defaults: yes,
    create: d => api.savePolicy('job-families', null, d), update: (r, d) => api.savePolicy('job-families', r.id, d), remove: r => api.deletePolicy('job-families', r.id),
    fields: [
      { name: 'name', label: 'Job Family', required: true, list: true }, { name: 'code', label: 'Code', list: true },
      { name: 'active', label: 'Active', type: 'boolean', list: true },
      { name: 'positions', label: 'Positions', type: 'integer', list: true, form: false }, { name: 'staff', label: 'Employees', type: 'integer', list: true, form: false },
    ],
  }), [load]);
  return <Register res={res} canEdit={can(meta, 'job-families')} />;
}

export function JobPositions() {
  const [meta] = usePyMeta();
  const [setup, load] = usePolicy(r => r.positions);
  const res = useMemo(() => ({
    key: 'job_positions', label: 'Position', plural: 'Positions', title: r => r.name, load, defaults: yes,
    create: d => api.savePolicy('job-positions', null, d), update: (r, d) => api.savePolicy('job-positions', r.id, d), remove: r => api.deletePolicy('job-positions', r.id),
    search: ['name', 'family'],
    fields: [
      { name: 'name', label: 'Position', required: true, list: true },
      { name: 'family_id', label: 'Job Family', type: 'select', required: true, options: opt(setup?.families), detail: false },
      { name: 'family', label: 'Job Family', list: true, form: false },
      { name: 'active', label: 'Active', type: 'boolean', list: true },
      { name: 'staff', label: 'Employees', type: 'integer', list: true, form: false },
    ],
  }), [load, setup]);
  return <Register res={res} canEdit={can(meta, 'job-positions')} />;
}

export function GradeEligibility() {
  const [meta] = usePyMeta();
  const [setup, load] = usePolicy(r => r.eligibility);
  const grades = (setup?.grades || []).map(g => ({ value: String(g.id), label: `${g.code}${g.level !== null && g.level !== undefined ? ` · level ${g.level}` : ''}` }));
  const positions = (setup?.positions || []).map(p => ({ value: String(p.id), label: `${p.family} › ${p.name}` }));
  const res = useMemo(() => ({
    key: 'grade_eligibility', label: 'Position Grade Eligibility', plural: 'Position Grade Eligibility', title: r => r.policy_name, load, defaults: yes,
    create: d => api.savePolicy('grade-eligibility', null, d), update: (r, d) => api.savePolicy('grade-eligibility', r.id, d),
    remove: r => api.deletePolicy('grade-eligibility', r.id),
    search: ['employee_category', 'family', 'position', 'policy_name'],
    fields: [
      { name: 'employee_category', label: 'Employee Category', type: 'select', required: true, options: setup?.categories || [], list: true },
      { name: 'family', label: 'Job Family', list: true, form: false },
      { name: 'position_id', label: 'Position', type: 'select', required: true, options: positions, detail: false },
      { name: 'position', label: 'Position', list: true, form: false },
      { name: 'min_grade_id', label: 'Lowest Allowed Grade', type: 'select', required: true, options: grades, detail: false },
      { name: 'min_grade', label: 'Lowest Allowed Grade', list: true, form: false },
      { name: 'max_grade_id', label: 'Highest Allowed Grade', type: 'select', required: true, options: grades, detail: false },
      { name: 'max_grade', label: 'Highest Allowed Grade', list: true, form: false },
      { name: 'default_grade_id', label: 'Default Grade', type: 'select', options: grades, detail: false },
      { name: 'default_grade', label: 'Default Grade', list: true, form: false },
      { name: 'policy_name', label: 'Benefit Policy', required: true, list: true },
      { name: 'active', label: 'Active', type: 'boolean', list: true },
    ],
  }), [load, setup]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Which grades an employee category and position may be placed on (by grade level). A grade outside the range is kept only with a
        grade exception reason on the employee's Financial Details.
      </div>
      <Register res={res} canEdit={can(meta, 'grade-eligibility')} />
    </>
  );
}

export function BenefitRules() {
  const [meta] = usePyMeta();
  const [setup, load] = usePolicy(r => r.rules);
  const res = useMemo(() => ({
    key: 'benefit_rules', label: 'Benefit Eligibility Rule', plural: 'Benefit Eligibility Rules', title: r => r.benefit, load, statusField: 'status',
    create: d => api.savePolicy('benefit-rules', null, d), update: (r, d) => api.savePolicy('benefit-rules', r.id, d), remove: r => api.deletePolicy('benefit-rules', r.id),
    search: ['benefit', 'family', 'status', 'note'],
    fields: [
      { name: 'target_id', label: 'Benefit / Allowance', type: 'select', required: true, options: setup?.targets || [], detail: false },
      { name: 'benefit', label: 'Benefit / Allowance', list: true, form: false },
      { name: 'family_id', label: 'Job Family (blank = all)', type: 'select', options: opt(setup?.families), detail: false },
      { name: 'family', label: 'Job Family', list: true, form: false },
      { name: 'status', label: 'Eligibility', type: 'select', required: true, options: setup?.statuses || [], pill: true, list: true },
      { name: 'min_grade_level', label: 'From Grade Level (optional)', type: 'integer', list: true },
      { name: 'note', label: 'Note', list: true },
    ],
  }), [load, setup]);
  return (
    <>
      <div className="alert info" style={{ marginBottom: 12 }}>
        Shown on the employee's Financial Details. A family's rule beats the rule for all families; a "from grade level" rule makes lower grades
        Not Eligible. Not Eligible also blocks that allowance or benefit in Compensation. The payroll calculation of Benefits Setup entitlements is
        not changed (grade flags decide those).
      </div>
      <Register res={res} canEdit={can(meta, 'benefit-rules')} />
    </>
  );
}
